/**
 * Minimal safe markdown rendering (U15) — the same grammar as the API's
 * print template (U14 print-html.ts): headings, -/* and ordered lists,
 * **bold**, *italic*, `code`, paragraphs. Input is HTML-escaped FIRST; the
 * transformer introduces only its own tags, so the resulting string is safe
 * for dangerouslySetInnerHTML. Keep both implementations aligned.
 */
import React from 'react';

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function mdToHtml(md: string): string {
  const inline = (text: string): string =>
    text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  const lines = escapeHtml(md.replace(/\r\n/g, '\n')).split('\n');
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  let paragraph: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length > 0) {
      out.push(`<p>${inline(paragraph.join('<br/>'))}</p>`);
      paragraph = [];
    }
  };
  const closeList = (): void => {
    if (list !== null) {
      out.push(`</${list}>`);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const ordered = /^\d+[.)]\s+(.*)$/.exec(line);

    if (line.trim() === '') {
      flushParagraph();
      closeList();
    } else if (heading) {
      flushParagraph();
      closeList();
      const level = Math.min(heading[1].length + 2, 6);
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
    } else if (bullet) {
      flushParagraph();
      if (list !== 'ul') {
        closeList();
        out.push('<ul>');
        list = 'ul';
      }
      out.push(`<li>${inline(bullet[1])}</li>`);
    } else if (ordered) {
      flushParagraph();
      if (list !== 'ol') {
        closeList();
        out.push('<ol>');
        list = 'ol';
      }
      out.push(`<li>${inline(ordered[1])}</li>`);
    } else {
      closeList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  closeList();
  return out.join('\n');
}

/** Render a markdown string as a styled block. */
export function Markdown({ text, className }: { text: string; className?: string }): JSX.Element {
  return (
    <div
      className={className ? `md ${className}` : 'md'}
      // Safe: mdToHtml escapes its input before adding our own tags.
      dangerouslySetInnerHTML={{ __html: mdToHtml(text) }}
    />
  );
}
